import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { API_TAGS } from '@/shared/api';
import {
  applySampleFilter,
  createSample,
  deleteSample,
  fetchSample,
  fetchSampleConversations,
  fetchSamples,
  updateSample,
} from './api';
import type { SampleConversationsParams, SampleCreateRequest, SampleListParams, SampleUpdateRequest } from './types';

/**
 * Query keys for samples
 */
export const sampleKeys = {
  all: [API_TAGS.SAMPLES] as const,
  lists: () => [...sampleKeys.all, 'list'] as const,
  list: (params?: SampleListParams) => [...sampleKeys.lists(), { ...params }] as const,
  details: () => [...sampleKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...sampleKeys.details(), id] as const,
  conversations: (id: string | number) => [...sampleKeys.all, 'conversations', id] as const,
  conversationsList: (id: string | number, params?: SampleConversationsParams) =>
    [...sampleKeys.conversations(id), { ...params }] as const,
};

/**
 * Hook to fetch a list of samples
 */
export const useSamples = (params?: SampleListParams) => {
  return useQuery({
    queryKey: sampleKeys.list(params),
    queryFn: () => fetchSamples(params),
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to fetch samples with infinite scroll
 */
export const useInfiniteSamples = (params?: SampleListParams & { limit?: number; offset?: number }) => {
  const limit = params?.limit || 50;
  const initialPageParam = Math.floor((params?.offset || 0) / limit);

  const query = useInfiniteQuery({
    queryKey: sampleKeys.list(params),
    queryFn: ({ pageParam }) => fetchSamples({ ...params, limit, offset: pageParam * limit }),
    initialPageParam,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const totalPages = Math.ceil((lastPage.total || 0) / limit);
      const nextPage = lastPageParam + 1;
      return nextPage < totalPages ? nextPage : undefined;
    },
  });

  const data = useMemo(
    () => ({
      total: query.data?.pages[0]?.total,
      samples: query.data?.pages.flatMap((p) => p.samples),
    }),
    [query.data],
  );

  return {
    ...query,
    data,
  };
};

/**
 * Hook to fetch a single sample by ID
 */
export const useSample = (id?: number | string) => {
  return useQuery({
    queryKey: sampleKeys.detail(id!),
    queryFn: () => fetchSample(id!),
    enabled: !!id && id !== 'create',
  });
};

/**
 * Hook to create a sample
 */
export const useCreateSample = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: SampleCreateRequest) => createSample(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sampleKeys.lists() });
    },
  });
};

/**
 * Hook to update a sample
 */
export const useUpdateSample = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: number | string; payload: SampleUpdateRequest }) => updateSample(id, payload),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: sampleKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: sampleKeys.lists() });
    },
  });
};

/**
 * Hook to delete a sample
 */
export const useDeleteSample = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => deleteSample(id),
    onSuccess: (_, deletedId) => {
      queryClient.invalidateQueries({ queryKey: sampleKeys.lists() });
      queryClient.removeQueries({ queryKey: sampleKeys.detail(deletedId) });
    },
  });
};

/**
 * Hook to fetch conversations in a sample
 * Compatible with usePaginatedQuery
 */
export const useSampleConversations = (params: { sampleId: number } & SampleConversationsParams) => {
  const { sampleId, ...queryParams } = params;
  return useQuery({
    queryKey: sampleKeys.conversationsList(sampleId, queryParams),
    queryFn: () => fetchSampleConversations(sampleId, queryParams),
    enabled: !!sampleId,
    placeholderData: keepPreviousData,
  });
};

/**
 * Hook to fetch conversations with infinite scroll
 */
export const useInfiniteSampleConversations = (
  id?: number | string,
  params?: SampleConversationsParams & { limit?: number; offset?: number },
) => {
  const limit = params?.limit || 50;
  const initialPageParam = Math.floor((params?.offset || 0) / limit);

  const query = useInfiniteQuery({
    queryKey: sampleKeys.conversationsList(id!, params),
    queryFn: ({ pageParam }) => fetchSampleConversations(id!, { ...params, limit, offset: pageParam * limit }),
    initialPageParam,
    placeholderData: keepPreviousData,
    enabled: !!id && id !== 'create',
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const totalPages = Math.ceil((lastPage.total || 0) / limit);
      const nextPage = lastPageParam + 1;
      return nextPage < totalPages ? nextPage : undefined;
    },
  });

  const data = useMemo(
    () => ({
      total: query.data?.pages[0]?.total,
      conversations: query.data?.pages.flatMap((p) => p.conversations),
    }),
    [query.data],
  );

  return {
    ...query,
    data,
  };
};

/**
 * Hook to apply filter to a sample
 */
export const useApplySampleFilter = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number | string) => applySampleFilter(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: sampleKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: sampleKeys.conversations(id) });
      queryClient.invalidateQueries({ queryKey: sampleKeys.lists() });
    },
  });
};
